// TODO: SMT-1374 — add unit tests for transformActivitySummaryResponse
import { resolveRows } from './presets'
import { CALC_IDS } from './constants'

function findCalc (calculations, id) {
  return calculations?.find(c => c.id === id && c.$type !== 'error')
}

export default function transformActivitySummaryResponse (response, config) {
  const orionData = response?.data ?? response
  const calcs = orionData?.calculations
  if (!calcs) return { rows: [], dateRange: null }

  const resolvedRows = resolveRows(config)

  const firstCalc = calcs[0]
  const dateRange = firstCalc
    ? { startDate: firstCalc.startDate, endDate: firstCalc.endDate }
    : null

  const showBondAccrual = !!config.bondAccrual

  let bmv = findCalc(calcs, CALC_IDS.BEGINNING_MARKET_VALUE)?.value ?? 0
  let emv = findCalc(calcs, CALC_IDS.ENDING_MARKET_VALUE)?.value ?? 0
  const beginningBondAccrual = findCalc(calcs, CALC_IDS.BEGINNING_BOND_ACCRUAL)?.value ?? 0
  const endingBondAccrual = findCalc(calcs, CALC_IDS.ENDING_BOND_ACCRUAL)?.value ?? 0

  if (!showBondAccrual) {
    bmv += beginningBondAccrual
    emv += endingBondAccrual
  }

  // Build activity lookup: name -> { value, activityOption }
  // NOTE: Keyed by activityOption.name which is constrained by Orion's response format.
  // If two rows share the same display name, the second will overwrite the first.
  // A more robust approach would match by position/index, but that requires a broader refactor.
  const activityCalc = findCalc(calcs, CALC_IDS.ACTIVITY_SUMMARY)
  const activityMap = new Map()

  activityCalc?.calculations?.forEach((calculation) => {
    const activityOption = calculation.contextChange?.activityOption
    const name = activityOption?.name ?? activityOption?.details?.[0]?.activityType ?? 'Unknown'
    const transTotal = findCalc(calculation.calculations, CALC_IDS.TRANSACTION_TOTAL)
    const value = transTotal?.value ?? 0
    activityMap.set(name, { value, activityOption })
  })

  // Build per-key activity value lookup for MV Change filtering
  const activityValuesByKey = new Map()
  let totalFromAllActivity = 0
  resolvedRows.forEach((r) => {
    if (r.rowType !== 'activity') return
    const match = activityMap.get(r.name)
    const val = match?.value ?? 0
    activityValuesByKey.set(r.key, val)
    totalFromAllActivity += val
  })

  const valuationValues = {
    [CALC_IDS.BEGINNING_MARKET_VALUE]: bmv,
    [CALC_IDS.ENDING_MARKET_VALUE]: emv,
    [CALC_IDS.BEGINNING_BOND_ACCRUAL]: beginningBondAccrual,
    [CALC_IDS.ENDING_BOND_ACCRUAL]: endingBondAccrual
  }

  const valuationCalcId = {
    'beginning-market-value': CALC_IDS.BEGINNING_MARKET_VALUE,
    'ending-market-value': CALC_IDS.ENDING_MARKET_VALUE,
    'beginning-bond-accrual': CALC_IDS.BEGINNING_BOND_ACCRUAL,
    'ending-bond-accrual': CALC_IDS.ENDING_BOND_ACCRUAL
  }

  const rows = []

  resolvedRows.forEach((rowDef) => {
    switch (rowDef.rowType) {
      case 'valuation': {
        const calcId = valuationCalcId[rowDef.key]
        const value = calcId != null ? (valuationValues[calcId] ?? 0) : 0
        rows.push({
          key: rowDef.key,
          name: rowDef.name,
          value,
          type: 'valuation',
          format: 'value',
          drillable: false
        })
        break
      }

      case 'activity': {
        const match = activityMap.get(rowDef.name)
        rows.push({
          key: rowDef.key,
          name: rowDef.name,
          value: match?.value ?? 0,
          type: 'activity',
          format: 'value',
          drillable: true,
          activityOption: match?.activityOption
        })
        break
      }

      case 'derived': {
        let value = 0
        let drillable = false
        let activityOptions = null
        if (rowDef.derivation === 'mvChange') {
          const includedKeys = Array.isArray(rowDef.includedActivityKeys)
            ? new Set(rowDef.includedActivityKeys)
            : null
          let activityTotal = totalFromAllActivity
          if (includedKeys) {
            activityTotal = 0
            activityValuesByKey.forEach((val, key) => {
              if (includedKeys.has(key)) activityTotal += val
            })
          }
          if (showBondAccrual) {
            value = (emv + endingBondAccrual) -
              (activityTotal + (beginningBondAccrual + bmv))
          } else {
            value = emv - (activityTotal + bmv)
          }
          const contributingOptions = []
          resolvedRows.forEach((r) => {
            if (r.rowType !== 'activity') return
            if (includedKeys && !includedKeys.has(r.key)) return
            const match = activityMap.get(r.name)
            if (match?.activityOption) contributingOptions.push(match.activityOption)
          })
          if (contributingOptions.length) {
            drillable = true
            activityOptions = contributingOptions
          }
        }
        const derivedRow = {
          key: rowDef.key,
          name: rowDef.name,
          value,
          type: 'derived',
          format: 'value',
          drillable
        }
        if (activityOptions) derivedRow.activityOptions = activityOptions
        rows.push(derivedRow)
        break
      }

      case 'performance': {
        const perfCalcId = `${CALC_IDS.PERFORMANCE_RETURN}_${rowDef.method}`
        const perfCalc = findCalc(calcs, perfCalcId)
        const perfValue = perfCalc?.performance ?? null
        rows.push({
          key: rowDef.key,
          name: rowDef.name,
          value: perfValue,
          type: 'performance',
          format: 'performance',
          drillable: false
        })
        break
      }

      default:
        break
    }
  })

  return { rows, dateRange }
}
